import request from '../../utils/request';
import type { Result } from '../../models/general';

/**
 * Hourly pay + per-shift approval — `api/ShiftPayApprovals` (docs/hourly-pay-spec.md §7).
 *
 * Hand-written, like `overtimeApprovals.ts`: `src/models/*` mirrors server DTOs file by file and this
 * controller is new. Field names are the spec's, verbatim — the spec is the contract both lanes code
 * against, so a rename belongs in the spec first.
 *
 * ⚠️ The wire OMITS null properties entirely, so every optional field must tolerate an absent key.
 * ⚠️ `utils/request.ts` REJECTS every non-2xx with the parsed body, so 400/403/409/423 arrive in the
 *    caller's `catch`. Read codes with `apiErrorsOf` / `shiftPayErrorCode`, never `.message`.
 */

export type ShiftPayKind = 'Scheduled' | 'Unscheduled';

export type ShiftPayStatus = 'Pending' | 'Approved' | 'Rejected' | 'AutoApproved';

/** `status` filter on `My` and `Queue`. Decided = Approved + Rejected + AutoApproved. */
export type ShiftPayStatusFilter = 'Pending' | 'Decided' | 'All';

export type ShiftPayGroupBy = 'employeeWeek' | 'day';

export type ShiftPayAction = 'approve' | 'reject' | 'revert';

/**
 * §7.1 — the closed set of attention codes. Adding one is a contract change (spec first). The client
 * MUST render an unknown code as a generic "Needs attention", so the type stays open (`string`).
 */
export const KNOWN_ATTENTION_REASONS = [
  'earlyClockIn',
  'lateClockIn',
  'earlyClockOut',
  'lateClockOut',
  'unscheduled',
  'openSession',
  'outOfBounds',
  'overMaximum',
  'belowMinimum',
  'restDay',
  'publicHoliday',
  'noHourlyRate',
  'reopened',
] as const;

export type KnownAttentionReason = (typeof KNOWN_ATTENTION_REASONS)[number];

/** §7.3 — stable `Error.Code` strings. */
export type ShiftPayErrorCode =
  | 'noteRequired'
  | 'noHourlyRate'
  | 'hasOpenSession'
  | 'notApprover'
  | 'selfApproval'
  | 'alreadyDecided'
  | 'concurrencyConflict'
  | 'payPeriodLocked';

export interface ShiftPaySession {
  shiftId: number;
  /** Employee wall clock, ISO without offset. */
  clockInLocal: string;
  clockOutLocal?: string;
  clockInUtc: string;
  clockOutUtc?: string;
  breakMinutes: number;
  /** Out-of-bounds status of that side (e.g. 'Pending' | 'Approved' | 'Rejected'); absent = none. */
  outOfBoundsIn?: string;
  outOfBoundsOut?: string;
}

/** §7.1 `ShiftPayApprovalItemView`. */
export interface ShiftPayItem {
  id: number;
  userId: string;
  userName: string;
  kind: ShiftPayKind;
  /** `yyyy-MM-dd`, employee-local. */
  workDate: string;
  scheduledStartLocal?: string;
  scheduledEndLocal?: string;
  scheduledStartUtc?: string;
  scheduledEndUtc?: string;
  sessions: ShiftPaySession[];
  workedMinutes: number;
  paidMinutes: number;
  premiumMinutes: number;
  status: ShiftPayStatus;
  isClean: boolean;
  attentionReasons: string[];
  hasOpenSession: boolean;
  decidedByName?: string;
  decidedOnUtc?: string;
  decisionNote?: string;
  reopenReason?: string;
  /** CO-814 — approved without a human decision (money: HR's bypass does not cover shift pay). */
  autoApproved?: boolean;
  autoApprovalReason?: 'ApprovalBypass' | 'WorkflowAutoApproval' | 'PolicyNoApprovalRequired' | null;
  /** CO-814 — who a pending item waits on; only on the employee's own list (`My`). */
  pendingWith?: 'Manager' | 'Hr' | 'Chief' | 'Unroutable' | null;
  /** Present ONLY for HR and for the item's own employee (salary data). */
  hourlyRate?: number;
  amount?: number;
  currencyCode?: string;
  /** Queue only. */
  groupKey?: string;
  /** Queue only. False => render no decision controls. */
  canDecide?: boolean;
}

/** §7.2 Queue group header — computed over the WHOLE filter, never paged. */
export interface ShiftPayGroup {
  key: string;
  userId?: string;
  userName?: string;
  /** Group bounds, server-computed (`HourlyWeekStartDay` for employeeWeek). Clients never compute weeks. */
  from: string;
  to: string;
  totalMinutes: number;
  pendingCount: number;
  attentionCount: number;
  cleanCount: number;
}

export interface ShiftPayQueue {
  groups: ShiftPayGroup[];
  items: ShiftPayItem[];
  total: number;
}

export interface ShiftPayMyList {
  items: ShiftPayItem[];
  total: number;
}

export interface ShiftPayMySummary {
  approvedMinutes: number;
  pendingMinutes: number;
  rejectedMinutes: number;
  approvedAmount?: number;
  currencyCode?: string;
}

export interface ShiftPayQueueQuery {
  userId?: string;
  from?: string;
  to?: string;
  status?: ShiftPayStatusFilter;
  /** true / false / undefined (= both). */
  clean?: boolean;
  groupBy?: ShiftPayGroupBy;
  page?: number;
  pageSize?: number;
}

export interface ShiftPayMyQuery {
  from?: string;
  to?: string;
  status?: ShiftPayStatusFilter;
  page?: number;
  pageSize?: number;
}

export interface ShiftPayDecideInput {
  action: ShiftPayAction;
  note?: string;
  overridePreviousDecision?: boolean;
}

export interface ShiftPayBulkFilter {
  userId?: string;
  from?: string;
  to?: string;
  cleanOnly?: boolean;
}

/** Exactly one of `ids` / `filter`. The server caps a batch at 1,000. */
export interface ShiftPayBulkInput {
  ids?: number[];
  filter?: ShiftPayBulkFilter;
  action: ShiftPayAction;
  note?: string;
  overridePreviousDecision?: boolean;
}

export interface ShiftPayBulkResult {
  id: number;
  ok: boolean;
  code?: string;
}

function queryString(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, unknown][]) {
    if (value === undefined || value === null || value === '') continue;
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export async function getMyShiftPay(query: ShiftPayMyQuery): Promise<Result<ShiftPayMyList>> {
  const response = await request.get<Result<ShiftPayMyList>>(
    `ShiftPayApprovals/My${queryString(query)}`,
  );
  return response.data;
}

/** `month` is `yyyy-MM`. */
export async function getMyShiftPaySummary(month: string): Promise<Result<ShiftPayMySummary>> {
  const response = await request.get<Result<ShiftPayMySummary>>(
    `ShiftPayApprovals/My/Summary${queryString({ month })}`,
  );
  return response.data;
}

export async function getShiftPayQueue(query: ShiftPayQueueQuery): Promise<Result<ShiftPayQueue>> {
  const response = await request.get<Result<ShiftPayQueue>>(
    `ShiftPayApprovals/Queue${queryString(query)}`,
  );
  return response.data;
}

export async function decideShiftPay(id: number, input: ShiftPayDecideInput): Promise<Result<ShiftPayItem>> {
  const response = await request.post<Result<ShiftPayItem>>(`ShiftPayApprovals/${id}/Decide`, input);
  return response.data;
}

export async function bulkDecideShiftPay(input: ShiftPayBulkInput): Promise<Result<ShiftPayBulkResult[]>> {
  const response = await request.post<Result<ShiftPayBulkResult[]>>('ShiftPayApprovals/Bulk', input);
  return response.data;
}
